<template>
  <!--
    Full-screen flex column glued to the visual viewport (see useVisualViewport).
    The whole page does not scroll; only the inner <main> does. This replaces a
    `position: fixed` input bar, which iOS WKWebView mispositions once the
    keyboard opens.
  -->
  <div
    class="bg-surface-default-low mood-warm fixed inset-x-0 top-0 flex flex-col overflow-hidden"
    :style="viewportStyle"
  >
    <Header ref="headerRef" @click="trackClicked" @new-chat="handleNewChat" />

    <!-- Loading state shown until the assistant is ready -->
    <main v-if="isLoading" class="flex flex-1 items-center justify-center">
      <RevSpinner />
    </main>

    <!-- Chat UI: the only scrolling element. min-h-0 lets this flex child shrink
         below its content so overflow-y-auto actually engages. overscroll-y-contain
         stops a swipe at the top/bottom from chaining to the WKWebView's root
         scroll view, which on iOS would otherwise rubber-band and drag the native
         modal down, exposing the background behind it. -->
    <main
      v-if="!isLoading"
      ref="scrollContainerRef"
      class="min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
    >
      <RevContainer>
        <div
          class="flex flex-col space-y-20 pt-60"
          :style="{
            paddingBottom: `${bottomPaddingPx}px`,
            transition: animatePadding
              ? 'padding-bottom 300ms ease-out'
              : 'none',
          }"
        >
          <div class="flex flex-col space-y-20">
            <template v-for="(event, index) in events" :key="index">
              <div
                v-if="event.type !== 'categorized_prompt_suggestion'"
                :ref="setEventRef(index)"
                class="flex flex-col py-10 scroll-mt-80"
              >
                <!-- User message -->
                <UserMessage
                  v-if="
                    event.type === 'message' && event.role === EVENT_ROLE_USER
                  "
                >
                  {{ event.content }}
                </UserMessage>

                <!-- Assistant message -->
                <AssistantMessage
                  v-else-if="
                    event.type === 'message' &&
                    event.role === EVENT_ROLE_ASSISTANT
                  "
                >
                  <MarkdownText :text="event.content" />
                </AssistantMessage>

                <!-- Product recommendations -->
                <section
                  v-else-if="event.type === 'product_recommendation'"
                  class="w-full"
                >
                  <ProductCardCarousel
                    :product-granularity
                    :products="toCarouselProducts(event.products)"
                    :tracking="{
                      list: 'ai_shopping_assistant',
                      source: 'ai_shopping_assistant_recommendation',
                    }"
                    @add-to-cart="handleAddToCart"
                    @link-click="trackProductClicked"
                  />
                </section>

                <!-- Prompt suggestions -->
                <div v-else-if="event.type === 'prompt_suggestion'">
                  <PromptSuggestions
                    :suggestions="event.prompts"
                    @click="handleSuggestionClick"
                  />
                </div>

                <!-- No products available -->
                <AssistantMessage
                  v-else-if="event.type === 'no_product_recommendation'"
                >
                  <MarkdownText :text="event.content" />
                </AssistantMessage>

                <p
                  v-if="index === 0"
                  class="mt-12 caption text-static-default-low"
                >
                  {{ i18n(translations.disclaimer) }}
                </p>
              </div>
            </template>
          </div>

          <div v-if="streamedMessage" class="flex flex-col py-10">
            <AssistantMessage>
              <MarkdownText :text="revealedMessage" />
            </AssistantMessage>
          </div>

          <div
            v-if="isWaitingForFirstChunk"
            class="text-static-default-low flex items-center space-x-6"
          >
            <Loader :variant="loaderVariant" />
          </div>
        </div>
      </RevContainer>
    </main>

    <!--
      Absolute (not fixed) so it anchors to the viewport-height root above,
      which already tracks the keyboard. bottom-0 therefore sits just above the
      keyboard when open, and above the home bar (safe-area inset) when closed.
    -->
    <aside
      v-if="!isLoading"
      ref="inputBarRef"
      class="absolute inset-x-0 bottom-0 pb-[calc(1.5rem+env(safe-area-inset-bottom))] safari-mobile-26:pb-[env(safe-area-inset-bottom)] z-10"
      :style="{
        background:
          'linear-gradient(to top, rgba(249, 247, 245, 0.8) 50%, rgba(249, 247, 245, 0) 100%)',
      }"
    >
      <!--
        Not RevContainer: the input keeps less horizontal padding (px-8) than
        the conversation (RevContainer px-24/lg:px-32), so it sits slightly
        wider than the messages and closer to the screen edges on narrow
        viewports — matching production. The max-width cap still prevents it
        from spanning full width on large viewports.
      -->
      <form
        class="mx-auto flex w-full max-w-[1184px] justify-center px-8"
        @submit.prevent="handleSubmit('typed')"
      >
        <InputWithSuggestions
          :categories="categorizedSuggestionEvent?.categories ?? []"
          class="w-full"
          :show-suggestions
          @suggestion-clicked="handleSuggestionClick"
        >
          <ChatInput
            v-model="inputText"
            :button-label="i18n(translations.sendMessage)"
            :label="i18n(translations.inputPlaceholder)"
            :with-suggestions="showSuggestions"
            @submit="handleSubmit('typed')"
          />
        </InputWithSuggestions>
      </form>
    </aside>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter, useRuntimeConfig } from '#imports'
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import ProductCardCarousel from '@backmarket/nuxt-layer-recommendation/ProductCardCarousel.vue'
import { algoliaHitToVariant } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevContainer } from '@ds/components/Container'
import { RevSpinner } from '@ds/components/Spinner'
import { useElementBounding } from '@vueuse/core'

import {
  type CategorizedPromptSuggestionEvent,
  type ChatEvent,
  type ChatRequestBody,
  EVENT_ROLE_ASSISTANT,
  EVENT_ROLE_USER,
  MESSAGE_ROLE_USER,
  type MessageInputMethod,
  type ProductRecommendationEvent,
  type StreamChunk,
  type StreamingEvent,
} from '~/api/shopping-assistant/types'

import { useVisualViewport } from '../../webviews/useVisualViewport'
import { useJSBridge } from '../composables/useJSBridge'
import { JS_BRIDGE_INJECTION_KEY } from '../composables/useJSBridge.injection-key'
import { useSeededLaunch } from '../composables/useSeededLaunch'
import { useShoppingAssistantActions } from '../composables/useShoppingAssistantActions'
import { useShoppingAssistantApi } from '../composables/useShoppingAssistantApi'
import { useShoppingAssistantContext } from '../composables/useShoppingAssistantContext'
import { useShoppingAssistantState } from '../composables/useShoppingAssistantState'
import { useShoppingAssistantTracking } from '../composables/useShoppingAssistantTracking'
import { useTypewriter } from '../composables/useTypewriter'
import { SHOPPING_ASSISTANT, SHOPPING_ASSISTANT_NEW } from '../routes.constants'

import translations from './ShoppingAssistant.translations'
import AssistantMessage from './components/AssistantMessage.vue'
import ChatInput from './components/ChatInput.vue'
import Header from './components/Header/Header.vue'
import InputWithSuggestions from './components/InputWithSuggestions.vue'
import Loader from './components/Loader/Loader.vue'
import MarkdownText from './components/MarkdownText.vue'
import PromptSuggestions from './components/PromptSuggestions.vue'
import UserMessage from './components/UserMessage.vue'

const config = useRuntimeConfig()

const { FF_SHOPPING_ASSISTANT_PRODUCT_GRANULARITY_PARENT } = config.public

const logger = useLogger()
const router = useRouter()
const route = useRoute()
const i18n = useI18n()
const { openErrorToast } = useTheToast()

const isStreamingEnabled = config.public.FF_SHOPPING_ASSISTANT_STREAMING

const {
  loaderVariant,
  inputText,
  inputWithSuggestions,
  events,
  isLoading,
  isProcessing,
  chatId,
  streamedMessage,
  resetState,
} = useShoppingAssistantState(
  typeof route.params.chatId === 'string' ? route.params.chatId : null,
)

const jsBridge = useJSBridge()
provide(JS_BRIDGE_INJECTION_KEY, jsBridge)
const { receivedEvent, sendReady, clearReceivedEvent } = jsBridge
const { addToCart } = useShoppingAssistantActions(jsBridge)

const eventRefs = new Map<number, HTMLElement>()
const headerRef = useTemplateRef<{ chromeEl: HTMLElement | null }>('headerRef')
const inputBarRef = useTemplateRef<HTMLElement>('inputBarRef')
// The only scrolling element; chat content scrolls inside it rather than the
// document, so all scroll math below is relative to this container.
const scrollContainerRef = useTemplateRef<HTMLElement>('scrollContainerRef')
const { height: headerHeight } = useElementBounding(
  computed(() => headerRef.value?.chromeEl ?? null),
)
const { height: inputBarHeight } = useElementBounding(inputBarRef)

// Keeps the chat root sized to the area above the on-screen keyboard.
const { style: viewportStyle } = useVisualViewport()

// Small visual gap above the input bar so chat content doesn't sit
// flush against it.
const BREATHING_ROOM_PX = 8
const inputClearance = computed(() => inputBarHeight.value + BREATHING_ROOM_PX)

// Bottom padding (in px) below the chat content. Large during a submit so
// scrollUserMessageToTop has room to lift the prompt under the header;
// trimmed after streaming so the user can't scroll past content into
// white space. See expandBottomPadding / trimBottomPadding.
const bottomPaddingPx = ref(0)
// Animate padding-bottom only when shrinking (after streaming). Expanding
// to make room for scrollUserMessageToTop must be instant — otherwise the
// smooth scroll fires against a still-growing maxScroll and lands short.
const animatePadding = ref(false)
// True while we expect the assistant's first chunk after a submit (or
// chat start) and no chunk has arrived yet. Drives the Loader so it
// doesn't flicker back on during the brief reactivity gap between
// flushStreamedMessage clearing streamedMessage and the finally that
// sets isProcessing = false.
const isWaitingForFirstChunk = ref(false)

// FIXME Backdoor to switch product granularity to "parent" in case the Add to cart button needs to be hidden.
const productGranularity = computed(() =>
  FF_SHOPPING_ASSISTANT_PRODUCT_GRANULARITY_PARENT ? 'parent' : 'backbox',
)

const { pageContext, entryPoint, seededMessage, isSeededLaunch } =
  useShoppingAssistantContext()

const {
  trackChatReady,
  trackMessageSent,
  trackClicked,
  trackProductClicked,
  trackProductRecommendationReceived,
} = useShoppingAssistantTracking(chatId, entryPoint)

const categorizedSuggestionEvent = computed(() =>
  events.value.find(
    (event): event is CategorizedPromptSuggestionEvent =>
      event.type === 'categorized_prompt_suggestion',
  ),
)

const showSuggestions = computed(
  () =>
    inputWithSuggestions.value &&
    categorizedSuggestionEvent.value !== undefined,
)

function toCarouselProducts(products: ProductRecommendationEvent['products']) {
  return products
    .filter((p) => p.listingID != null)
    .map((p) => ({
      ...algoliaHitToVariant(p),
      specialOfferType: p.special_offer_type,
    }))
}

function handleAddToCart({ status }: { status: string }) {
  if (status === 'success') {
    addToCart()
  } else if (status === 'error') {
    openErrorToast({ content: i18n(translations.addToCartError) })
  }
}

watch(receivedEvent, (newEvent) => {
  if (newEvent) {
    sendAndTrackMessage({ content: newEvent, inputMethod: 'suggested' })
    clearReceivedEvent()
  }
})

function setEventRef(index: number) {
  return (el: Element | ComponentPublicInstance | null) => {
    if (el) {
      eventRefs.set(index, el as HTMLElement)
    } else {
      eventRefs.delete(index)
    }
  }
}

const {
  startChat: apiStartChat,
  sendMessage: apiSendMessage,
  abortSendMessage,
  isSendMessageAborted,
  streamMessage: apiStreamMessage,
  streamStartChat: apiStreamStartChat,
} = useShoppingAssistantApi()

const {
  displayed: revealedMessage,
  drain: drainTypewriter,
  reset: resetTypewriter,
} = useTypewriter(streamedMessage)

function flushStreamedMessage() {
  if (streamedMessage.value) {
    processEvents([
      {
        type: 'message',
        role: EVENT_ROLE_ASSISTANT,
        content: streamedMessage.value,
      },
    ])
    streamedMessage.value = ''
  }
}

function handleStreamChunk(event: StreamChunk, skipTracking = false) {
  isWaitingForFirstChunk.value = false
  if (event.type === 'text_delta') {
    streamedMessage.value += event.content
  } else {
    flushStreamedMessage()
    processEvents([event], skipTracking)
  }
}

function processEvents(newEvents: ChatEvent[], skipTracking = false) {
  events.value.push(...newEvents)

  if (!skipTracking) {
    const receivedProducts = newEvents
      .filter(
        (event): event is ProductRecommendationEvent =>
          event.type === 'product_recommendation',
      )
      .flatMap(({ products }) => toCarouselProducts(products))

    trackProductRecommendationReceived(receivedProducts)
  }
}

// Reflect the freshly created chat id in the URL without a Vue Router navigation
// (history.replaceState avoids a remount; preserve history.state per Vue Router docs).
// URL sync is purely cosmetic — a failure here must never block using the created
// chat (which matters for deep-link seeded launches), so swallow and log it.
function syncUrlToChatId(id: string) {
  try {
    const newPath = router.resolve({
      name: SHOPPING_ASSISTANT,
      params: { chatId: id },
    }).href
    window.history.replaceState(window.history.state, '', newPath)
  } catch (error) {
    logger.error('[SHOPPING_ASSISTANT] Failed to sync chat id to URL', {
      error: error as Error,
      owners: ['bot-group-ai-shopping-assistant-front'],
    })
  }
}

async function startChat({
  chatId: existingChatId,
  canResumeLatest = false,
  withPageContext = false,
}: {
  chatId: string | null
  canResumeLatest?: boolean
  withPageContext?: boolean
}) {
  isWaitingForFirstChunk.value = true
  const body: ChatRequestBody = {
    context: 'Front Office User',
    by: config.public.ENVIRONMENT !== 'prod' ? 'tester' : 'user',
  }
  if (existingChatId === null) {
    // Page context (when present and allowed) takes precedence: it starts a
    // fresh chat scoped to the surface. Otherwise, resume the latest chat when
    // allowed. A seeded launch passes withPageContext without canResumeLatest,
    // so it stays force-new and never resumes.
    if (withPageContext && pageContext.value) {
      body.page_context = pageContext.value
    } else if (canResumeLatest) {
      body.resume_latest = true
    }
  }

  try {
    if (isStreamingEnabled) {
      isProcessing.value = true

      await new Promise<void>((resolve, reject) => {
        let eventReceived = false
        apiStreamStartChat(existingChatId, body, {
          onEvent: (event: StreamingEvent) => {
            eventReceived = true
            if (event.type === 'chat_created') {
              chatId.value = event.id
              syncUrlToChatId(event.id)
            } else {
              handleStreamChunk(event, !!existingChatId)
            }
          },
          onEnd: async (terminatedBySentinel: boolean) => {
            if (!terminatedBySentinel && !eventReceived) {
              reject(new Error('Unexpected non-SSE response'))

              return
            }
            await drainTypewriter()
            flushStreamedMessage()
            resolve()
          },
          onAbort: () => resolve(),
          onError: reject,
        })
      })
    } else {
      const { data } = await apiStartChat(existingChatId, body)

      if (!existingChatId) {
        chatId.value = data.id
        syncUrlToChatId(data.id)
      }

      processEvents(data.events, !!existingChatId)
    }

    trackChatReady()
  } catch (error) {
    const err = error as { status?: number }
    if (err.status === 404) {
      openErrorToast({
        content: i18n(translations.chatNotFound),
        actionLabel: i18n(translations.newChatButton),
        actionCallback: () => router.push({ name: SHOPPING_ASSISTANT_NEW }),
      })
    } else {
      logger.error('[SHOPPING_ASSISTANT] Failed to create chat', {
        error: error as Error,
        owners: ['bot-group-ai-shopping-assistant-front'],
      })
      openErrorToast({
        content: i18n(translations.loadChatError),
        actionLabel: i18n(translations.retryButton),
        actionCallback: () => {
          startChat({
            chatId: existingChatId,
            canResumeLatest,
            withPageContext,
          })
        },
      })
    }
  } finally {
    isLoading.value = false
    isProcessing.value = false
    isWaitingForFirstChunk.value = false
    loaderVariant.value = 'spinner'
    scrollToBottom().then(trimBottomPadding)
  }
}

async function sendMessage({
  content,
  inputMethod,
}: {
  content: string
  inputMethod: MessageInputMethod
}): Promise<void> {
  if (!chatId.value) return

  const currentChatId = chatId.value
  const userMessageIndex = events.value.length
  let loaderTimeout: ReturnType<typeof setTimeout> | null = null
  // The endpoint always expects MESSAGE_ROLE_USER, regardless of how the message was entered.
  // This may revert to a conditional role in the future.
  // The typed/suggested distinction is preserved for tracking only.
  const role = MESSAGE_ROLE_USER

  try {
    isProcessing.value = true
    isWaitingForFirstChunk.value = true

    // Add user message as a local event
    events.value.push({
      type: 'message',
      role: EVENT_ROLE_USER,
      content,
    })

    expandBottomPadding()
    scrollUserMessageToTop()

    loaderTimeout = setTimeout(() => {
      loaderVariant.value = 'text'
    }, 2000)

    if (isStreamingEnabled) {
      await new Promise<void>((resolve, reject) => {
        let eventReceived = false
        apiStreamMessage(
          currentChatId,
          { content, role },
          {
            onEvent: (event: StreamChunk) => {
              eventReceived = true
              handleStreamChunk(event)
            },
            onEnd: async (terminatedBySentinel: boolean) => {
              if (!terminatedBySentinel && !eventReceived) {
                reject(new Error('Unexpected non-SSE response'))

                return
              }
              await drainTypewriter()
              flushStreamedMessage()
              resolve()
            },
            onAbort: () => resolve(),
            onError: reject,
          },
        ).catch((err: Error) => {
          if (err.name === 'AbortError') resolve()
          else reject(err)
        })
      })
    } else {
      const data = await apiSendMessage(chatId.value, { content, role })
      processEvents(data.events)
    }
  } catch (error) {
    if (!isSendMessageAborted.value) {
      logger.error(
        `[SHOPPING_ASSISTANT] Failed to send message with inputMethod: ${inputMethod}`,
        {
          error: error as Error,
          owners: ['bot-group-ai-shopping-assistant-front'],
        },
      )
      openErrorToast({
        content: i18n(translations.sendMessageErrorContent),
        actionLabel: i18n(translations.retryButton),
        actionCallback: () => {
          events.value.splice(userMessageIndex, 1)
          sendMessage({ content, inputMethod })
        },
      })
    }
  } finally {
    if (loaderTimeout !== null) clearTimeout(loaderTimeout)
    isProcessing.value = false
    isWaitingForFirstChunk.value = false
    loaderVariant.value = 'spinner'
    trimBottomPadding()
  }
}

function getLastRenderedEventEl(): HTMLElement | null {
  for (let i = events.value.length - 1; i >= 0; i -= 1) {
    const el = eventRefs.get(i)
    if (el) return el
  }

  return null
}

// Offset of an element's bottom within the scroll container's content
// (i.e. its position in "scrollTop space"). Mirrors what `rect.bottom +
// window.scrollY` used to give in document space.
function eventBottomInContainer(
  el: HTMLElement,
  container: HTMLElement,
): number {
  return (
    el.getBoundingClientRect().bottom -
    container.getBoundingClientRect().top +
    container.scrollTop
  )
}

// Give the container enough room below the latest message that
// scrollUserMessageToTop can lift it all the way under the header.
function expandBottomPadding() {
  animatePadding.value = false
  const viewportHeight =
    scrollContainerRef.value?.clientHeight ?? window.innerHeight
  bottomPaddingPx.value = viewportHeight - headerHeight.value
}

// Shrink the padding so the user can't scroll past the last event into
// empty white space, while still allowing them to reach the end of a
// long response with comfortable input clearance.
async function trimBottomPadding() {
  await nextTick()
  const container = scrollContainerRef.value
  const lastEl = getLastRenderedEventEl()
  if (!container || !lastEl) {
    bottomPaddingPx.value = inputClearance.value

    return
  }
  const lastEventBottom = eventBottomInContainer(lastEl, container)
  // maxScroll must allow (a) the user to stay where they currently are and
  // (b) reaching the end of the last event with inputClearance breathing
  // room above the input bar.
  const minMaxScrollForLastEvent =
    lastEventBottom + inputClearance.value - container.clientHeight
  const desiredMaxScroll = Math.max(
    container.scrollTop,
    minMaxScrollForLastEvent,
  )
  animatePadding.value = true
  bottomPaddingPx.value = Math.max(
    inputClearance.value,
    desiredMaxScroll + container.clientHeight - lastEventBottom,
  )
}

function scrollToBottom(): Promise<void> {
  // Re-assert scrollTop for ~500ms to absorb late layout shifts from streamed
  // content. Targets the last event's bottom (above the input bar) rather
  // than the content end, since the bottom padding may be viewport-sized.
  return new Promise<void>((resolve) => {
    const scroll = () => {
      const container = scrollContainerRef.value
      const el = getLastRenderedEventEl()
      if (!container || !el) return
      const targetTop =
        eventBottomInContainer(el, container) -
        container.clientHeight +
        inputClearance.value
      container.scrollTo?.({ top: targetTop, behavior: 'instant' })
    }
    nextTick(() => {
      scroll()
      const interval = setInterval(scroll, 50)
      setTimeout(() => {
        clearInterval(interval)
        resolve()
      }, 500)
    })
  })
}

async function scrollUserMessageToTop() {
  await nextTick()
  const container = scrollContainerRef.value
  const el = eventRefs.get(events.value.length - 1)
  if (!container || !el) return
  // Lift the message so its top sits just below the fading header chrome.
  const delta =
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top -
    headerHeight.value
  container.scrollTo?.({ top: container.scrollTop + delta, behavior: 'smooth' })
}

async function sendAndTrackMessage({
  content,
  inputMethod,
}: {
  content: string
  inputMethod: MessageInputMethod
}) {
  await sendMessage({ content, inputMethod })

  trackMessageSent({ inputMethod })
}

// Auto-send the initial message of a seeded launch. A seeded launch opens on a
// specific question, so the generic chat-creation intro (greeting + starter
// suggestions, already streamed into `events` by now) no longer makes sense —
// drop all of it so the conversation starts on the user's message. The AI
// disclaimer is rendered separately (under the first event) and is intentionally
// kept. The suggestions container was already hidden at mount (see onMounted).
// Tags the message as 'seeded'.
async function sendSeededMessage(content: string) {
  events.value = []

  await sendAndTrackMessage({ content, inputMethod: 'seeded' })
}

const { maybeLaunch: maybeSeededLaunch } = useSeededLaunch({
  isSeededLaunch,
  seededMessage,
  startChat,
  sendSeededMessage,
})

// Event Handlers

async function handleSubmit(inputMethod: MessageInputMethod) {
  const content = inputText.value.trim()
  if (!content || !chatId.value || isProcessing.value) return

  inputWithSuggestions.value = false

  inputText.value = ''
  await sendAndTrackMessage({ content, inputMethod })
}

async function handleSuggestionClick(text: string) {
  // Remove all prompt suggestions from the events
  events.value = events.value.filter(
    (event) =>
      event.type !== 'prompt_suggestion' &&
      event.type !== 'categorized_prompt_suggestion',
  )

  inputText.value = text
  await handleSubmit('suggested')
}

// Called when the user confirms "new chat" in the header. The component is
// not remounted (history.replaceState avoids Vue Router navigation), so we
// reset state and start a fresh session in-place.
async function handleNewChat() {
  abortSendMessage()
  resetState()
  resetTypewriter()
  eventRefs.clear()

  // A user-initiated new chat is intentionally blank: no page context, no resume.
  await startChat({
    chatId: null,
    canResumeLatest: false,
    withPageContext: false,
  })
}

// Seed bottomPaddingPx from the input bar's first measurement, so chat content
// doesn't render flush against the fixed input bar before any submit happens.
const stopInitialPaddingWatcher = watch(inputBarHeight, (h) => {
  if (h > 0 && bottomPaddingPx.value === 0) {
    bottomPaddingPx.value = inputClearance.value
    stopInitialPaddingWatcher()
  }
})

// This page is a full-screen position:fixed layout, so its document never
// scrolls. Lock document-level overscroll while mounted so a swipe that starts
// outside the inner scroller (e.g. on the header or input bar) can't rubber-band
// the WKWebView root scroll view and drag the native modal down on iOS. The
// scope is the page lifecycle only — webview-default is shared with pages
// (help-center, generic webviews) that do rely on document scrolling, so we
// restore the previous value on unmount.
let previousOverscrollBehavior = ''
onMounted(() => {
  previousOverscrollBehavior = document.documentElement.style.overscrollBehavior
  document.documentElement.style.overscrollBehavior = 'none'
})
onUnmounted(() => {
  document.documentElement.style.overscrollBehavior = previousOverscrollBehavior
})

// Initialization
onMounted(async () => {
  try {
    // A seeded launch (deep link) takes over: force-new chat + optional initial
    // message. Otherwise fall back to the normal resume/create-on-mount path.
    if (isSeededLaunch.value) {
      // Suppress the categorized starter-suggestions container up front. The
      // create response still carries starter suggestions (discarded by
      // sendSeededMessage), and without this the blue suggestions container would
      // briefly flash in during the create stream before being cleared.
      inputWithSuggestions.value = false
    }
    const handled = await maybeSeededLaunch()
    if (!handled) {
      await startChat({
        chatId: chatId.value,
        canResumeLatest: true,
        withPageContext: true,
      })
    }
  } finally {
    sendReady()
  }
})

onUnmounted(() => {
  abortSendMessage()
})
</script>
